import { type ReactElement } from 'react';
import { styled, ToggleButtonGroup, toggleButtonGroupClasses, ToggleButtonGroupProps } from '@mui/material';
import { HiddenLegend, SegmentedControl } from '@figma/fpl-components';
import * as stylex from '@stylexjs/stylex';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { Tooltip, type TooltipProps } from '@/UI/Tooltip/Tooltip';
import { PropertyToggleButton } from './PropertyToggleButton';

const StyledPropertyToggleButtonGroup = styled(ToggleButtonGroup)(() => ({
  display: 'flex',
  border: `1px solid ${token.border}`,
  gap: '1px',
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    border: 'none',
    '&:hover': {
      border: 'none',
    },
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: 'none',
    },
  },
}));

export interface PropertyToggleButtonsProps<T extends string | number> extends Omit<
  ToggleButtonGroupProps,
  'value' | 'onChange' | 'exclusive' | 'aria-label'
> {
  /** The accessible name for the property toggle group. */
  'aria-label': string;
  /**
   * The selected value
   */
  value: T | null;
  options: Array<{
    'aria-label': string;
    disabled?: boolean;
    id?: string;
    label: ReactElement;
    tooltipText?: string;
    value: T;
  }>;
  /**
   * Called when the value changes
   */
  onChange: (value: T | null) => void;
  btnW?: number;
  btnH?: number;
  tooltipPlacement?: TooltipProps['placement'];
  allowDeselect?: boolean;
  /** Stretch the control to fill its container (an equal `1fr` column) instead of a fixed 88px. */
  fill?: boolean;
}

const optionKey = (value: string | number) => `${typeof value}:${String(value)}`;

export const PropertyToggleButtons = <T extends string | number>({
  value,
  options,
  onChange,
  btnW,
  btnH,
  tooltipPlacement = 'right',
  allowDeselect = false,
  fill = false,
  ...props
}: PropertyToggleButtonsProps<T>) => {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const handleChange = (_event: React.MouseEvent<HTMLElement>, newValue: T | null) => {
    if (allowDeselect) {
      onChange(newValue);
    } else if (newValue !== null) {
      onChange(newValue);
    }
  };

  if (isFplMigrationEnabled) {
    const selectedKey = value == null ? undefined : optionKey(value);
    return (
      <div {...stylex.props(fill ? styles.fplGroupFill : styles.fplGroup)}>
        <SegmentedControl.Root
          legend={<HiddenLegend>{props['aria-label']}</HiddenLegend>}
          value={selectedKey}
          readonly={props.disabled}
          onChange={(key) => {
            const nextValue = options.find((option) => optionKey(option.value) === key)?.value;
            if (nextValue !== undefined) onChange(nextValue);
          }}
        >
          {options.map((option) => {
            // The span, not the option, is the tooltip trigger. FPL's selected fill is an
            // `input:checked + .icon` rule, and an open tooltip injects a focus guard as the
            // next sibling of its trigger, which inside the option would break that adjacency.
            const segmentedOption = (
              <span key={optionKey(option.value)} {...stylex.props(styles.fplOption)}>
                <SegmentedControl.Option
                  value={optionKey(option.value)}
                  icon={option.label}
                  aria-label={option['aria-label']}
                  readonly={option.disabled ? true : undefined}
                />
              </span>
            );
            // The FPL SegmentedControl has no built-in tooltip, and options talk to the Root
            // via context rather than child iteration, so wrappers between them are safe.
            return option.tooltipText ? (
              <Tooltip key={optionKey(option.value)} title={option.tooltipText} placement={tooltipPlacement}>
                {segmentedOption}
              </Tooltip>
            ) : (
              segmentedOption
            );
          })}
        </SegmentedControl.Root>
      </div>
    );
  }

  return (
    <StyledPropertyToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      {...props}
      sx={fill ? { width: '100%', ...props.sx } : props.sx}
    >
      {options.map((option) => {
        const button = (
          <PropertyToggleButton
            disableRipple
            key={option.value}
            value={option.value}
            aria-label={option['aria-label'] || String(option.value)}
            btnW={btnW}
            btnH={btnH}
            id={option.id}
            disabled={option.disabled}
            sx={fill ? { flex: 1, width: 'auto', minWidth: 0 } : undefined}
          >
            {option.label}
          </PropertyToggleButton>
        );
        return option.tooltipText ? (
          <Tooltip
            key={option.value}
            title={option.tooltipText}
            placement={tooltipPlacement}
            enterDelay={800}
            enterNextDelay={800}
          >
            {button}
          </Tooltip>
        ) : (
          button
        );
      })}
    </StyledPropertyToggleButtonGroup>
  );
};

const styles = stylex.create({
  fplGroup: {
    flex: '0 0 88px',
    minWidth: 0,
    width: '88px',
  },
  fplGroupFill: {
    flex: 1,
    minWidth: 0,
    width: '100%',
  },
  fplOption: {
    display: 'inline-flex',
    flex: 1,
    minWidth: 0,
  },
});
